/**
 * button.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Base Button Component (shadcn/cva)
 *
 * This is the low-level primitive. For semantic variants with
 * built-in icon support, loading state, and role-based visibility
 * use the exported helpers from action-buttons.tsx instead.
 *
 * DESIGN CONTRACT:
 *   Radius:   10px (rounded-[10px]) on ALL sizes — no rounded-md, no rounded-xl
 *   Shadow:   0 1px 2px rgba(0,0,0,0.04) — same as inputs, flat not elevated
 *   Font:     13px medium (default/lg), 12px medium (sm)
 *   Height:   h-10 (40px) default · h-9 (36px) sm · h-11 (44px) lg
 *   Padding:  px-5 default · px-3 sm · px-6 lg
 *   Gap:      gap-2 (icon + label)
 *   Transition: all 150ms ease
 *
 * VARIANTS:
 *   default      → Primary blue  (#008ED3 → #0077B3 hover → #006699 active)
 *   outline      → White bg, border #E2E8F0 → #F8FAFC hover
 *   ghost        → Transparent → gray-100 hover (gray text)
 *   ghost_blue   → Transparent → #EBF6FD hover (blue text)
 *   destructive  → Red (#EF4444 → #DC2626 hover)
 *   link         → Underline on hover, no bg
 *
 * SIZES:
 *   default → h-10 px-5 text-[13px]
 *   sm      → h-9  px-4 text-[12px]
 *   lg      → h-11 px-6 text-[13px]
 *   icon    → w-9 h-9 (square, 36px — use for toolbar icon-only buttons)
 *   icon_sm → w-8 h-8 (square, 32px — use for table-row actions)
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./utils";

// ─── Shared base ─────────────────────────────────────────────
const BASE = [
  // Layout
  "inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0",
  // Shape — always 10px, matching inputs
  "rounded-[10px]",
  // Typography
  "text-[13px] font-medium",
  // Interaction
  "cursor-pointer select-none",
  "transition-all duration-150",
  "outline-none",
  // Focus ring — keep accessible but subtle
  "focus-visible:ring-2 focus-visible:ring-[#008ED3]/40 focus-visible:ring-offset-1",
  // Active — satisfying press-down scale (all variants)
  "active:scale-[0.97]",
  // Disabled
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40",
  // SVG inside — normalize
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  // Standard DS shadow — same as inputs (0.04 opacity = barely visible lift)
  "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
].join(" ");

const buttonVariants = cva(BASE, {
  variants: {
    variant: {
      // ── Primary: filled blue ──
      default:
        "bg-[#008ED3] text-white border-none " +
        "hover:bg-[#0077B3] " +
        "active:bg-[#006699]",

      // ── Outline: white + border ──
      outline:
        "bg-white text-[#374151] border border-[#E2E8F0] " +
        "hover:bg-[#F8FAFC] hover:border-[#94A3B8] " +
        "active:bg-[#F1F5F9] active:border-[#94A3B8]",

      // ── Ghost (gray): no bg, gray text ──
      ghost:
        "bg-transparent text-[#6B7280] border-none shadow-none " +
        "hover:bg-gray-100 hover:text-gray-900 " +
        "active:bg-gray-200",

      // ── Ghost blue: no bg, primary text ──
      ghost_blue:
        "bg-transparent text-[#008ED3] border-none shadow-none " +
        "hover:bg-[#EBF6FD] " +
        "active:bg-[#C5E4F3]",

      // ── Destructive: red filled ──
      destructive:
        "bg-[#EF4444] text-white border-none " +
        "hover:bg-[#DC2626] " +
        "active:bg-[#B91C1C]",

      // ── Link: underline text ──
      link:
        "bg-transparent text-[#008ED3] border-none shadow-none underline-offset-4 " +
        "hover:underline hover:text-[#0077B3]",

      // ── Legacy aliases (kept for backward-compat) ──
      primary_blue:
        "bg-[#008ED3] text-white border-none " +
        "hover:bg-[#0077B3] " +
        "active:bg-[#006699]",

      secondary:
        "bg-white text-[#374151] border border-[#E2E8F0] " +
        "hover:bg-[#F8FAFC] hover:border-[#94A3B8] " +
        "active:bg-[#F1F5F9]",
    },

    size: {
      // Standard CTA (Apply, Save, Submit, Add)
      default: "h-10 px-5 py-2 has-[>svg]:px-4",
      // Compact (filter bar buttons, toolbar CTAs)
      sm:      "h-9  px-4 py-1.5 text-[12px] has-[>svg]:px-3",
      // Large (hero CTAs, modal primary actions)
      lg:      "h-11 px-6 py-2.5 has-[>svg]:px-5",
      // Square icon-only — 36px (filter toggles, header icons)
      icon:    "w-9 h-9 p-0",
      // Square icon-only — 32px (table row actions, card actions)
      icon_sm: "w-8 h-8 p-0",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

// ─── Component ───────────────────────────────────────────────
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };